export const PERMISSIONS = {
  VIEW_ALL_ORG_BUILDINGS: "VIEW_ALL_ORG_BUILDINGS",
  VIEW_ASSIGNED_BUILDINGS: "VIEW_ASSIGNED_BUILDINGS",
  VIEW_ASSIGNED_PROJECTS: "VIEW_ASSIGNED_PROJECTS",
  // Gates access to the Documents tab (list + timeline views). Distinct from
  // BUILDING_VIEW_DOCUMENTS (row-level RLS concern) — see franklin-data-pipeline#67.
  VIEW_DOCUMENTS_LIST_TIMELINE: "VIEW_DOCUMENTS_LIST_TIMELINE",
  ADD_DATA: "ADD_DATA",
  MODIFY_DATA: "MODIFY_DATA",
  DELETE_DATA: "DELETE_DATA",
  ADD_PROJECT_FILES: "ADD_PROJECT_FILES",
  DELETE_PROJECT_FILES: "DELETE_PROJECT_FILES",
  MANAGE_ORG_PERMISSIONS: "MANAGE_ORG_PERMISSIONS",
  MANAGE_PROJECT_PERMISSIONS: "MANAGE_PROJECT_PERMISSIONS",
  MANAGE_ORG_INVITES: "MANAGE_ORG_INVITES",
  UPLOAD_DOCUMENTS: "UPLOAD_DOCUMENTS",
  // Chatbot access — org-scoped, same uniform pattern as everything else.
  VIEW_CHATBOT: "VIEW_CHATBOT",
  USE_CHATBOT: "USE_CHATBOT",
  // Project-scoped (subcontractor + future project-scoped roles)
  PROJECT_READ: "PROJECT_READ",
  PROJECT_WRITE_FILES: "PROJECT_WRITE_FILES",
  PROJECT_WRITE_FOLDERS: "PROJECT_WRITE_FOLDERS",
  PROJECT_SOFT_DELETE: "PROJECT_SOFT_DELETE",
  PROJECT_GENERATE_SHARE_LINK: "PROJECT_GENERATE_SHARE_LINK",
  PROJECT_VIEW_LOG: "PROJECT_VIEW_LOG",
  // Project-scoped UPDATE on documents/folders. Required for rename and other
  // file-metadata edits — RLS gates documents UPDATE on this OR org MODIFY_DATA.
  MODIFY_PROJECT_FILES: "MODIFY_PROJECT_FILES",
  // Building-shell tab visibility
  BUILDING_VIEW_DASHBOARD: "BUILDING_VIEW_DASHBOARD",
  BUILDING_VIEW_DOCUMENTS: "BUILDING_VIEW_DOCUMENTS",
  BUILDING_VIEW_PROJECTS: "BUILDING_VIEW_PROJECTS",
  BUILDING_VIEW_DOCUMENTS_LOG: "BUILDING_VIEW_DOCUMENTS_LOG",
  // (No BUILDING_VIEW_3D_MODEL: the 3D viewer is open to all org members; the
  // IFC documents it loads are RLS-scoped to what each user can already see.)
  // Building data sidebar — VIEW gates seeing the right-side data panel,
  // MANAGE gates editing attribute values in it. Building-scoped (WHA-3488);
  // replaced the never-seeded, platform-admin-only VIEW_DATA_PANEL code.
  VIEW_BUILDING_DATA_SIDEBAR: "VIEW_BUILDING_DATA_SIDEBAR",
  MANAGE_BUILDING_DATA_SIDEBAR: "MANAGE_BUILDING_DATA_SIDEBAR",
  // Org-shell create actions. Granted to org_owner via the has_permission
  // bypass (no DB grant required); subcontractors lack them and so don't
  // see the corresponding buttons.
  CREATE_BUILDINGS: "CREATE_BUILDINGS",
  CREATE_PROJECTS: "CREATE_PROJECTS",
  // Project metadata edits (rename, description). Distinct from
  // MODIFY_PROJECT_FILES which is project-scoped file UPDATE.
  MODIFY_PROJECTS: "MODIFY_PROJECTS",
  // Mark a project active / inactive (a grouping label, not archiving).
  // org_owner + org_admin only; the building_projects trigger enforces it.
  MANAGE_PROJECT_STATUS: "MANAGE_PROJECT_STATUS",
  // PM module read access (issues + comments). Project-scoped per
  // WHA-3406: granted to org_owner (covered by has_permission bypass too)
  // and subcontractor. Per-row RLS enforces project membership via
  // user_project_assignments. Not used directly by any frontend gate
  // today — /issues is gated by VIEW_PM_TOOL — but kept in sync with the
  // backend permission code so usePermissions enumerates it correctly.
  VIEW_PM: "VIEW_PM",
  // PM module write access (create/update/delete issues + own comments).
  // Same scoping as VIEW_PM. RLS is the enforcement layer.
  MANAGE_PM: "MANAGE_PM",
  // PM tool — module-level UI gate for the /issues route + sidebar. Granted to
  // org_owner + subcontractor in WHA-3301; per-issue access is enforced by the
  // project-scoped VIEW_PM / MANAGE_PM RLS policies. No platform-admin bypass.
  VIEW_PM_TOOL: "VIEW_PM_TOOL",
  // Drawing Compare — module-level UI gate for the /buildings/:id/drawings and
  // /tools/drawings routes, the Models sidebar item, and the DWG view/compare
  // actions in the document lists. Granted to all three org roles in
  // franklin-data-pipeline migration 20260803105531; replaced the temporary
  // isPlatformAdmin gate the tool shipped with. No platform-admin bypass — the
  // /tools HUB itself stays staff-only, but the tool it hosts does not.
  VIEW_DRAWING_COMPARE_TOOL: "VIEW_DRAWING_COMPARE_TOOL",
  // Project brief (lähteülesanne). Both are `requires_context` (project-scoped,
  // like VIEW_PM / MANAGE_PM): VIEW gates the Brief tab and page, MANAGE gates
  // writing appendices, accepting or dismissing Franklin's suggestions,
  // running a check and confirming a version. RLS + the brief_* RPCs are the
  // boundary; these are affordances.
  VIEW_BRIEFS: "VIEW_BRIEFS",
  MANAGE_BRIEFS: "MANAGE_BRIEFS",
  // Per-project drawing-compare access. requires_context, so has_permission()
  // additionally needs a user_project_assignments row and a project_id on the
  // document. This is what request_drawing_geometry enforces server-side; the
  // frontend gates on the module code above rather than re-deriving it.
  VIEW_DRAWING_COMPARE: "VIEW_DRAWING_COMPARE",
  // Saving comparisons and pinning issues on them. Same project scoping as
  // VIEW_DRAWING_COMPARE; enforced by drawing_comparison* RLS.
  MANAGE_DRAWING_COMPARE: "MANAGE_DRAWING_COMPARE",
  // Opening a project's IFC models in the 3D model viewer (/buildings/:id/model).
  // Per project and member-configurable (franklin-data-pipeline #318); see
  // canUseModelViewerOnProject for the pre-migration rule.
  USE_MODEL_VIEWER: "USE_MODEL_VIEWER",
  // Digital signing — gates the Sign button in the document multi-select bar.
  // Org-scoped; combined with the org-level organisations.signing_enabled flag.
  // org_owner has it via the has_permission bypass; admins grant to other roles.
  ALLOW_SIGNING: "ALLOW_SIGNING",
  // Contacts page (/buildings/:id/contacts). Both project-scoped
  // (requires_context) and granted to all three org roles in
  // franklin-data-pipeline migration 20260928091240. VIEW gates the page and
  // the sidebar tab; MANAGE gates adding contacts. Which entries a holder may
  // edit (their own, or ones they entered; owners/admins any) is decided by
  // project_contacts RLS and reported per row as get_contacts().can_edit.
  VIEW_CONTACTS: "VIEW_CONTACTS",
  MANAGE_CONTACTS: "MANAGE_CONTACTS",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// Org-scoped role tiers: org_owner > org_admin > subcontractor.
// NOTE: platform admin (user_profiles.is_platform_admin / the /admin panel) is a
// SEPARATE Franklin-staff system and is NOT an org role — never list it here.
export const ROLES = {
  ORG_OWNER: "org_owner",
  ORG_ADMIN: "org_admin",
  SUBCONTRACTOR: "subcontractor",
} as const;

export type AppRole = (typeof ROLES)[keyof typeof ROLES];

// Customer-facing labels. The `subcontractor` role code is internal and stays as
// is everywhere in code/DB; users only ever read "Member".
const ROLE_LABELS: Record<string, string> = {
  org_owner: "Owner",
  org_admin: "Admin",
  subcontractor: "Member",
};

export function formatRole(role: string): string {
  return ROLE_LABELS[role] ?? role;
}
